import {
  Component,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  ChatHistoryGroup,
  ChatHistoryItem,
  ChatbotHistoryService,
} from '../chatbot-history.service';
import {
  IChatbotHistoryApiService,
  LazyLoadQueryParams,
  SortDirection,
} from '../chatbot-history-api.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AppNetworkService } from '../../../shared/network/app-network.service';
import { RetryButtonComponent } from '../../../shared/components/retry-button/retry-button.component';
import { DateFormatPipe } from '../../../shared/date/date-format.pipe';
import { HighlightPipe } from '../../../shared/pipes/highlight.pipe';

/** Number of sessions to load per page (initial and "load more") */
const PAGE_SIZE = 30;

@Component({
    selector: 'sabis-history-side-panel',
    templateUrl: './history-side-panel.component.html',
    styleUrl: './history-side-panel.component.scss',
    imports: [CommonModule, FormsModule, RetryButtonComponent, DateFormatPipe, HighlightPipe]
})
export class HistorySidePanelComponent implements OnInit, OnDestroy {
  /** Whether the panel is collapsed */
  @Input() public isCollapsed: boolean = false;

  /** The currently active session ID to highlight */
  public activeSessionId = input<string | null>(null);

  /** Set to true by the parent when connected to the chatbot API; triggers initial load */
  public isReadyToLoad = input(false);

  /** Emits when a chat history item is selected */
  @Output() public chatSelected = new EventEmitter<ChatHistoryItem>();

  /** Emits when edit is clicked on a chat item */
  @Output() public chatEdit = new EventEmitter<ChatHistoryItem>();

  /** Tracks whether the app is online */
  public isNetworkOnline: boolean = true;

  /** Reference to edit input elements */
  @ViewChildren('editInput') editInputs!: QueryList<ElementRef<HTMLInputElement>>;
  /** ID of the item currently being edited (null if none) */
  public editingItemId: string | null = null;
  /** Current value of the edit input */
  public editingTitle: string = '';
  /** Loading state for saving title */
  public isSavingTitle: boolean = false;

  /** Chat history groups organized by date */
  public historyGroups: ChatHistoryGroup[] = [];

  /** Search query for filtering chats */
  public searchQuery: string = '';

  /** Currently selected chat item ID */
  public selectedChatId: string | null = null;

  /** Loading state for history data */
  public isLoading: boolean = false;

  /** Error state for initial history load */
  public hasLoadError: boolean = false;

  /** Loading state for "load more" */
  public isLoadMoreLoading: boolean = false;

  /** True when the last fetched page had a full PAGE_SIZE (so there might be more) */
  private _lastPageWasFull: boolean = false;

  /** True when the API returned fewer than PAGE_SIZE rows (no more pages) */
  private _noMoreSessions: boolean = false;

  /** Whether more sessions can be loaded (inferred from full-page responses) */
  public get hasMore(): boolean {
    return this._lastPageWasFull && !this._noMoreSessions;
  }

  /** Subject to handle component destruction */
  private _destroy$ = new Subject<void>();

  /** Subject for debounced search: when user types we request the backend with the search query */
  private _searchSubject = new Subject<string>();

  private _hasInitialHistoryLoad = false;
  private _firstMessage = false;

  constructor(
    @Inject('ChatbotHistoryApiService') private _historyApiService: IChatbotHistoryApiService,
    private _historyService: ChatbotHistoryService,
    private _networkService: AppNetworkService,
    private _elementRef: ElementRef
  ) {
    effect(() => {
      if (this.isReadyToLoad()) {
        this.selectedChatId = this.activeSessionId();
        // reload session each time the activeSessionId is changed to get it from the backend
        if (!this._hasInitialHistoryLoad || this._firstMessage) {
          this._hasInitialHistoryLoad = true;
          this._firstMessage = false;
          this.loadSessions();
        }
      }
    });
  }

  /**
   * Stops click events from bubbling up to the document,
   * preventing the panel from collapsing when clicked inside.
   */
  @HostListener('click', ['$event'])
  public onHostClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  /**
   * Listens for clicks on the document and collapses the panel
   * when the click occurs outside of the host element.
   */
  @HostListener('document:click', ['$event.target'])
  public onDocumentClick(target: HTMLElement): void {
    if (!this.isCollapsed && target instanceof HTMLElement && !this._elementRef.nativeElement.contains(target)) {
      this.isCollapsed = true;
    }
  }

  public ngOnInit(): void {
    this._searchSubject
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        takeUntil(this._destroy$)
      )
      .subscribe(() => this.loadSessions());

    // Subscribe to network status changes
    this._networkService.online$
      .pipe(takeUntil(this._destroy$))
      .subscribe(isOnline => {
        this.isNetworkOnline = isOnline;
        // If connection is regained and user had typed a search query while offline, re-trigger the search
        if (isOnline && this.searchQuery.trim()) {
          this.selectedChatId = this.activeSessionId();
          this.loadSessions();
        }
      });

    // Subscribe to chatbot history events to refresh the list when the first message is sent
    this._historyService.firstMessageSent$
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._firstMessage = true;
        this.loadSessions();
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Builds lazy-load params for the API
   */
  private buildQueryParams(toSkip: number, rowsCount: number): LazyLoadQueryParams {
    return {
      includeCount: false,
      toSkip,
      rowsCount,
      sortField: 'date',
      sortDirection: SortDirection.Descending,
      filter: null,
    };
  }

  /**
   * Returns total number of sessions currently loaded across all groups
   */
  private getLoadedSessionCount(): number {
    return this.historyGroups.reduce((sum, g) => sum + g.items.length, 0);
  }

  /**
   * Merges new groups into existing history (by date); appends items to existing groups or adds new groups
   */
  private mergeGroupsIntoHistory(newGroups: ChatHistoryGroup[]): void {
    for (const newGroup of newGroups) {
      const key = newGroup.date.toDateString();
      const existing = this.historyGroups.find(g => g.date.toDateString() === key);
      if (existing) {
        existing.items.push(...newGroup.items);
      } else {
        this.historyGroups.push({ ...newGroup });
        this.historyGroups.sort((a, b) => b.date.getTime() - a.date.getTime());
      }
    }
  }

  /** Returns the effective search query to apply to backend filtering */
  private getEffectiveSearchQuery(): string {
    const query = this.searchQuery;
    return query.length >= 3 ? query : '';
  }

  /**
  * Loads history groups from the API (initial load: first PAGE_SIZE sessions)
  */
  private loadSessions(): void {
    this.isLoading = true;
    this.hasLoadError = false;
    this._noMoreSessions = false;
    const params = this.buildQueryParams(0, PAGE_SIZE);
    const searchQuery = this.getEffectiveSearchQuery();
    this._historyApiService.getSessions(searchQuery, params)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (page) => {
          this._lastPageWasFull = page.rows.length === PAGE_SIZE;
          this._noMoreSessions = page.rows.length < PAGE_SIZE;
          this.historyGroups = this._historyService.mapSessionsToGroups(page.rows);
          this.isLoading = false;
          this.hasLoadError = false;
        },
        error: () => {
          this.isLoading = false;
          this.hasLoadError = true;
        },
      });
  }

  /**
   * Loads more sessions on demand (next PAGE_SIZE)
   */
  public loadMore(): void {
    // stop if loading or nothing to load
    if (this.isLoadMoreLoading || !this.hasMore) {
      return;
    }

    const toSkip = this.getLoadedSessionCount();
    this.isLoadMoreLoading = true;
    const params = this.buildQueryParams(toSkip, PAGE_SIZE);
    const searchQuery = this.getEffectiveSearchQuery();
    this._historyApiService.getSessions(searchQuery, params)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (page) => {
          this._lastPageWasFull = page.rows.length === PAGE_SIZE;
          this._noMoreSessions = page.rows.length < PAGE_SIZE;
          const newGroups = this._historyService.mapSessionsToGroups(page.rows);
          this.mergeGroupsIntoHistory(newGroups);
          this.isLoadMoreLoading = false;
        },
        error: () => {
          this.isLoadMoreLoading = false;
        },
      });
  }

  /**
   * Reloads history groups from the API service
   */
  public reloadHistory(): void {
    this.loadSessions();
  }

  /**
   * Called when the user changes the search input. Triggers a debounced backend request with the search query.
   */
  public onSearchChange(value: string): void {
    this.searchQuery = value;
    this._searchSubject.next(value);
  }

  /**
   * Clears the search input and refreshes the sessions list
   */
  public clearSearch(): void {
    if (!this.searchQuery) {
      return;
    }

    this.searchQuery = '';
    this._searchSubject.next('');
  }

  /**
   * Toggles the collapsed state of the side panel
   */
  public toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  /**
   * Toggles the expanded state of a history group
   * @param group The group to toggle
   */
  public toggleGroup(group: ChatHistoryGroup): void {
    group.isExpanded = !group.isExpanded;
  }

  /**
   * chat session clicked
   * @param item
   * @returns
   */
  public selectChatClick(item: ChatHistoryItem): void {
    if (!this.isNetworkOnline) {
      return;
    }
    this.selectChat(item);
  }

  /**
   * Selects a chat history item
   * @param item The chat item to select
   */
  public selectChat(item: ChatHistoryItem): void {
    this.selectedChatId = item.llmSessionId;
    this.chatSelected.emit(item);
  }

  /**
   * Handles edit button click on a chat item - starts inline editing
   * @param event Mouse event to stop propagation
   * @param item The chat item to edit
   */
  public onEditChat(event: MouseEvent, item: ChatHistoryItem): void {
    event.stopPropagation();
    this.startEditing(item);
  }

  /**
   * Starts inline editing for a chat item
   * @param item The chat item to edit
   */
  public startEditing(item: ChatHistoryItem): void {
    this.editingItemId = item.llmSessionId;
    this.editingTitle = item.title;
    // Focus the input after Angular renders it
    setTimeout(() => {
      const input = this.editInputs?.first?.nativeElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  /**
   * Cancels the current edit operation
   */
  public cancelEdit(): void {
    this.editingItemId = null;
    this.editingTitle = '';
  }

  /**
   * Saves the edited title
   * @param item The chat item being edited
   */
  public saveEdit(item: ChatHistoryItem): void {
    const newTitle = this.editingTitle.trim();
    if (!newTitle || newTitle === item.title) {
      this.cancelEdit();
      return;
    }

    this.isSavingTitle = true;
    this._historyApiService.updateSessionTitle(item.sessionId, newTitle)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          // Update the item title in the local state
          item.title = newTitle;
          this.editingItemId = null;
          this.editingTitle = '';
          this.isSavingTitle = false;
        },
        error: () => {
          this.isSavingTitle = false;
          // Keep editing state so user can retry or cancel
        }
      });
  }

  /**
   * Handles keydown events on the edit input
   * @param event Keyboard event
   * @param item The chat item being edited
   */
  public onEditKeydown(event: KeyboardEvent, item: ChatHistoryItem): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveEdit(item);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  /**
   * Handles blur event on the edit input - saves the edit
   * @param item The chat item being edited
   */
  public onEditBlur(item: ChatHistoryItem): void {
    // Small delay to allow click events on save button to fire first
    setTimeout(() => {
      if (this.editingItemId === item.llmSessionId && !this.isSavingTitle) {
        this.saveEdit(item);
      }
    }, 150);
  }
}
